import DOMPurify from "dompurify";

/**
 * Whitelist für die im News-Text erlaubten Tags/Attribute.
 * Nur Fett, Kursiv, Größe, Zeilenumbruch und einfache Struktur.
 */
const ALLOWED_TAGS = [
  "b", "strong", "i", "em", "u", "br", "p", "div", "span", "font", "a",
];
const ALLOWED_ATTR = ["size", "style", "href", "target", "rel"];

// Nur diese CSS-Properties dürfen in inline styles überleben
const ALLOWED_CSS_PROPS = new Set([
  "font-size", "font-weight", "font-style", "text-align",
  "text-decoration", "text-decoration-line",
]);

const CONFIG = {
  ALLOWED_TAGS,
  ALLOWED_ATTR,
  ALLOWED_URI_REGEXP: /^(?:https?:\/\/|mailto:|tel:)/i,
  FORBID_TAGS: ["script", "iframe", "object", "embed", "link", "style", "form", "input", "meta"],
  FORBID_ATTR: ["onerror", "onclick", "onload", "onmouseover", "onfocus", "onblur", "class", "id"],
};

/**
 * Filtert inline-style-Attribute: nur die whitelisted CSS-Properties überleben.
 * Verhindert dass "border-image-source", "hsl(var(--border))" usw. den Editor zerbrechen.
 */
function stripUnwantedStyles(html) {
  if (!html || typeof document === "undefined") return html;
  const doc = new DOMParser().parseFromString(`<div>${html}</div>`, "text/html");
  const root = doc.body.firstChild;

  const process = (node) => {
    if (node.nodeType !== 1) return; // nur Element-Nodes
    // style-Attribut filtern
    const style = node.getAttribute && node.getAttribute("style");
    if (style) {
      const cleaned = style
        .split(";")
        .map((s) => s.trim())
        .filter(Boolean)
        .map((decl) => {
          const idx = decl.indexOf(":");
          if (idx < 0) return null;
          const prop = decl.slice(0, idx).trim().toLowerCase();
          const val = decl.slice(idx + 1).trim();
          if (!ALLOWED_CSS_PROPS.has(prop)) return null;
          // CSS-Variablen und "url(" verbieten
          if (/\bvar\s*\(/i.test(val) || /url\s*\(/i.test(val) || /expression\s*\(/i.test(val)) return null;
          return `${prop}: ${val}`;
        })
        .filter(Boolean)
        .join("; ");
      if (cleaned) node.setAttribute("style", cleaned);
      else node.removeAttribute("style");
    }
    // Klassen und IDs immer entfernen (falls DOMPurify sie durchgelassen haben sollte)
    node.removeAttribute && node.removeAttribute("class");
    node.removeAttribute && node.removeAttribute("id");
    // Externe Links immer in neuem Tab + abgesichert öffnen
    if (node.tagName === "A") {
      const href = node.getAttribute("href") || "";
      if (/^https?:\/\//i.test(href)) {
        node.setAttribute("target", "_blank");
        node.setAttribute("rel", "noopener noreferrer");
      }
    }
    // Rekursion
    Array.from(node.childNodes).forEach(process);
  };
  process(root);
  return root.innerHTML;
}

/**
 * Wandelt nackte URLs in Textknoten automatisch in klickbare Links um
 * (damit z. B. eingefügte Adressen wie https://… sofort anklickbar sind).
 * Bereits verlinkte Texte (innerhalb von <a>) bleiben unberührt.
 */
const URL_RE = /(https?:\/\/[^\s<>"']*[^\s<>"'.,;:!?)…])/g;

function linkifyTextNodes(root) {
  if (typeof document === "undefined") return;
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  const targets = [];
  while (walker.nextNode()) {
    const n = walker.currentNode;
    if (n.parentElement && n.parentElement.closest("a")) continue;
    URL_RE.lastIndex = 0;
    if (URL_RE.test(n.nodeValue)) targets.push(n);
  }
  targets.forEach((n) => {
    const text = n.nodeValue;
    const frag = document.createDocumentFragment();
    let last = 0;
    text.replace(URL_RE, (m, url, off) => {
      frag.appendChild(document.createTextNode(text.slice(last, off)));
      const a = document.createElement("a");
      a.setAttribute("href", url);
      a.setAttribute("target", "_blank");
      a.setAttribute("rel", "noopener noreferrer");
      a.textContent = url;
      frag.appendChild(a);
      last = off + m.length;
      return m;
    });
    frag.appendChild(document.createTextNode(text.slice(last)));
    n.parentNode.replaceChild(frag, n);
  });
}

/**
 * Bereitet HTML zum Anzeigen (öffentlich + Vorschau) auf.
 * Streng gefiltert.
 */
export function renderNewsHtml(html) {
  if (!html) return "";
  const purified = DOMPurify.sanitize(html, CONFIG);
  const cleaned = stripUnwantedStyles(purified);
  // Nackte URLs automatisch verlinken
  const doc = new DOMParser().parseFromString(`<div>${cleaned}</div>`, "text/html");
  linkifyTextNodes(doc.body.firstChild);
  return doc.body.firstChild.innerHTML;
}

/**
 * Bereitet HTML zum Laden/Bearbeiten im Editor auf.
 * Identisch zum Render-Sanitize.
 */
export function sanitizeForEditor(html) {
  return renderNewsHtml(html);
}

/**
 * Bereitet Clipboard-HTML (paste event) auf: entfernt Word/Google-Docs-Müll,
 * lässt nur unsere erlaubten Tags/Styles durch.
 */
export function sanitizePasteHtml(html) {
  if (!html) return "";
  // Word/Google-Docs bringen viele conditional comments und leere spans mit — grob vorreinigen
  let pre = html
    .replace(/<!--[\s\S]*?-->/g, "")
    .replace(/<o:p\b[^>]*>[\s\S]*?<\/o:p>/gi, "")
    .replace(/<\/?(html|head|body|meta|title|xml|script|style)\b[^>]*>/gi, "");
  return renderNewsHtml(pre);
}

/**
 * Prüft ob der Rich-Text-Inhalt effektiv leer ist
 * (nur Whitespace / leere <p><br></p>-Struktur).
 */
export function isRichTextEmpty(html) {
  if (!html) return true;
  const plain = html
    .replace(/<br\s*\/?>/gi, "")
    .replace(/<\/?[^>]+(>|$)/g, "")
    .replace(/&nbsp;/gi, "")
    .trim();
  return plain === "";
}
